<template>
  <v-container>
    <v-card>
      <v-card-title>
        <span class="headline">Customer usage report</span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="fmtFromDate"
                  label="From date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  dense
                  clearable
                  @click:clear="searchCriteria.fromDate = null"></v-text-field>
              </template>
              <v-date-picker
                v-model="searchCriteria.fromDate"
                @input="fromDateMenu=false;setEndOfMonth();">
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col>
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="fmtToDate"
                  label="To date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  dense
                  clearable
                  @click:clear="searchCriteria.toDate = null"></v-text-field>
              </template>
              <v-date-picker
                v-model="searchCriteria.toDate"
                @input="toDateMenu=false">
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              v-model="model"
              no-filter
              :items="entries"
              :loading="isLoading"
              :search-input.sync="search"
              chips
              hide-details
              hide-selected
              item-text="displayName"
              item-value="id"
              label="Search for a customer..."
              return-object
              solo>
              <template v-slot:selection="{ attr, on, item, selected }">
                <v-chip
                  v-bind="attr"
                  :input-value="selected"
                  color="blue-grey"
                  class="white--text"
                  v-on="on">
                  <v-icon left>
                    mdi-account
                  </v-icon>
                  <span v-text="item.displayName" class="pr-2"></span>
                  <span v-text="item.mobileNumber" class="pr-2"></span>
                  <span>Id: {{item.id}}</span>
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-avatar
                  color="indigo"
                  class="headline font-weight-light white--text">
                  {{ item.lastName.charAt(0) }}
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title v-text="item.displayName"></v-list-item-title>
                  <v-list-item-subtitle v-text="item.mobileNumber"></v-list-item-subtitle>
                  <v-list-item-subtitle>Id: {{item.id}}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon>mdi-account</v-icon>
                </v-list-item-action>
              </template>
              <template v-slot:append>
                <v-btn icon color="primary" v-on:click="clearCustomer">
                  <v-icon>mdi-eraser</v-icon>
                </v-btn>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row class="px-4">
          <v-btn color="primary" class="d-print-none" @click="runReport()">Run Report</v-btn>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row class="pt-8">
      <v-container fluid fill-height>
        <v-card width="100vw">
          <v-data-table
              dense
              hide-default-footer
              disable-pagination
              :headers="headers"
              :items="reportData">
          </v-data-table>
        </v-card>
      </v-container>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import storeConstants from '@/store/storeConstants';

export default {
  name: 'CustomerUsage',
  data: () => ({
    reportData: [],
    fromDateMenu: false,
    toDateMenu: false,
    searchCriteria: {
      fromDate: null,
      toDate: null,
      customerId: null,
    },
    entries: [],
    isLoading: false,
    model: null,
    search: null,
    headers: [
      { text: 'Customer', value: 'customerName' },
      { text: 'Contact phone', value: 'contactPhone' },
      { text: 'Contact email', value: 'contactEmail' },
      { text: 'Booking Count', value: 'bookingCount' },
    ],
  }),
  mounted() {
    this.setCriteria();
  },
  methods: {
    ...mapActions([
      storeConstants.GET_REPORT_CUSTOMER_USAGE,
      storeConstants.SEARCH_CUSTOMERS]),
    setCriteria() {
      this.setStartOfMonth();
      this.setEndOfMonth();
    },
    runReport() {
      this.GET_REPORT_CUSTOMER_USAGE(this.searchCriteria)
        .then((data) => {
          this.reportData = data;
        },
        (error) => {
          this.SET_ALERT_DETAILS({ alertText: error.response.data.error, showAlert: true });
        })
        .catch(() => {
        });
    },
    formatDate(date) {
      if (!date) {
        return null;
      }
      return this.$moment(date).format(this.DISPLAY_DATE_FORMAT);
    },
    setStartOfMonth() {
      const startOfMonth = this.$moment().startOf('month').format(this.ISO_8601_FORMAT);
      this.searchCriteria.fromDate = startOfMonth;
    },
    setEndOfMonth() {
      const endOfMonth = this.$moment(this.searchCriteria.fromDate).endOf('month').format(this.ISO_8601_FORMAT);
      this.searchCriteria.toDate = endOfMonth;
    },
    clearCustomer() {
      this.searchCriteria.customerId = null;
      this.entries = [];
    },
  },
  computed: {
    ...mapGetters([
      storeConstants.SHORT_DATE_FORMAT,
      storeConstants.DISPLAY_DATE_FORMAT,
      storeConstants.ISO_8601_FORMAT,
      storeConstants.CUSTOMER_SEARCH_RESULTS]),
    fmtFromDate() {
      return this.formatDate(this.searchCriteria.fromDate);
    },
    fmtToDate() {
      return this.formatDate(this.searchCriteria.toDate);
    },
  },
  watch: {
    model(val) {
      if (val) {
        this.searchCriteria.customerId = val.id;
      } else {
        this.searchCriteria.customerId = null;
      }
    },
    search(val) {
      // search text is valid
      if (!val || val.length < 4) return;

      // Items have already been requested
      if (this.isLoading) return;

      this.isLoading = true;

      this.SEARCH_CUSTOMERS({ searchText: val })
        .then(() => {
          this.count = this.CUSTOMER_SEARCH_RESULTS.length;
          this.entries = this.CUSTOMER_SEARCH_RESULTS;
          this.isLoading = false;
        },
        (error) => {
          this.SET_ALERT_DETAILS({ alertText: error.response.data.error, showAlert: true });
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
  },
};

</script>
